import { Check } from '@phosphor-icons/react';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';
import { uiText } from '../i18n';

export interface HistoryDay {
  /** YYYY-MM-DD */
  key: string;
  /** "Mo" */
  label: string;
  /** "28.9." */
  date: string;
  /** Spoken day, e.g. "Montag, 28. September" */
  fullLabel: string;
  done: number;
  total: number;
}

export interface DayHistoryProps {
  days: HistoryDay[];
  className?: string;
}

/**
 * The last days at a glance: one column per day, filled as far as the day's own tasks were done.
 * A full day gets a check. Days without tasks say "frei". Numbers are text for screen readers.
 */
export function DayHistory({ days, className }: DayHistoryProps) {
  return (
    <ol
      className={cn('grid gap-gap-xs', className)}
      style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}
    >
      {days.map((d) => {
        const full = d.total > 0 && d.done >= d.total;
        const share = d.total ? Math.min(1, d.done / d.total) : 0;
        return (
          <li key={d.key} className="flex flex-col items-center gap-gap-xs text-center">
            <span aria-hidden className="text-sm font-bold">
              {d.label}
            </span>
            <span
              aria-hidden
              className={cn(
                'relative flex h-16 w-full max-w-touch-min items-end overflow-hidden rounded-control',
                d.total ? 'bg-surface-sunken' : 'border-2 border-dashed border-border-subtle',
              )}
            >
              <span
                className={cn(
                  'w-full transition-all',
                  full ? 'bg-feedback-success' : 'bg-accent-tertiary',
                )}
                style={{ height: `${share * 100}%` }}
              />
              {full && (
                <Icon
                  icon={Check}
                  weight="bold"
                  size="md"
                  className="absolute inset-x-0 top-1 mx-auto text-text-on-action"
                />
              )}
            </span>
            <span aria-hidden className="text-xs text-text-secondary tabular-nums">
              {d.total ? `${d.done}/${d.total}` : uiText().free}
            </span>
            <span className="sr-only">
              {d.total
                ? uiText().dayDone(d.fullLabel, String(d.done), String(d.total))
                : uiText().dayEmpty(d.fullLabel)}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
